<template>
  <span v-if="!disabled" ref="stateLayer" class="pointer-events-none absolute inset-0 block">
    <span
      v-if="!showRipple"
      class="absolute inset-0 transition-colors"
      :class="[
        stateLayerType == 'surface' && 'group-hover:bg-primary/8 group-active:bg-primary/10',
        stateLayerType == 'surface-neutral' &&
          'group-hover:bg-on-surface/8 group-active:bg-on-surface/10',
        stateLayerType == 'primary' && 'group-hover:bg-on-primary/8 group-active:bg-on-primary/10',
        stateLayerType == 'on-primary' && 'group-hover:bg-primary/8 group-active:bg-primary/10',
        stateLayerType == 'secondary' &&
          'group-hover:bg-on-secondary/8 group-active:bg-on-secondary/10',
        stateLayerType == 'tertiary' &&
          'group-hover:bg-on-tertiary/8 group-active:bg-on-tertiary/10',
        stateLayerType == 'primary-container' &&
          'group-hover:bg-on-primary-container/8 group-active:bg-on-primary-container/10',
        stateLayerType == 'secondary-container' &&
          'group-hover:bg-on-secondary-container/8 group-active:bg-on-secondary-container/10',
        stateLayerType == 'tertiary-container' &&
          'group-hover:bg-on-tertiary-container/8 group-active:bg-on-tertiary-container/10',
        stateLayerType == 'brandColor' && brandColorStateLayerClass,
      ]"
    ></span>
    <Transition
      enter-active-class="transition-transform duration-2000 ease-fast-effect"
      enter-from-class="scale-0"
      enter-to-class="scale-100"
      leave-from-class="scale-0"
      leave-to-class="scale-0"
      leave-active-class="transition-none"
    >
      <span
        v-if="showRipple && !forcePressed"
        class="pointer-events-none absolute aspect-square w-[300%] -translate-x-1/2 -translate-y-1/2 rounded-full"
        :class="[
          stateLayerType == 'surface' && 'bg-primary/10',
          stateLayerType == 'surface-neutral' && 'bg-on-surface/10',
          stateLayerType == 'primary' && 'bg-on-primary/10',
          stateLayerType == 'secondary' && 'bg-on-secondary/10',
          stateLayerType == 'tertiary' && 'bg-on-tertiary/10',
          stateLayerType == 'primary-container' && 'bg-on-primary-container/10',
          stateLayerType == 'secondary-container' && 'bg-on-secondary-container/10',
          stateLayerType == 'tertiary-container' && 'bg-on-tertiary-container/10',
          stateLayerType == 'brandColor' && brandColorRippleClass,
        ]"
        :style="{
          left: `${clickPosition.x}px`,
          top: `${clickPosition.y}px`,
        }"
      ></span>
    </Transition>
    <span
      v-if="forcePressed"
      class="pointer-events-none absolute aspect-square w-[300%] -translate-x-1/2 -translate-y-1/2 rounded-full"
      :class="[
        stateLayerType == 'surface' && 'bg-primary/10',
        stateLayerType == 'surface-neutral' && 'bg-on-surface/10',
        stateLayerType == 'primary' && 'bg-on-primary/10',
        stateLayerType == 'secondary' && 'bg-on-secondary/10',
        stateLayerType == 'tertiary' && 'bg-on-tertiary/10',
        stateLayerType == 'primary-container' && 'bg-on-primary-container/10',
        stateLayerType == 'secondary-container' && 'bg-on-secondary-container/10',
        stateLayerType == 'tertiary-container' && 'bg-on-tertiary-container/10',
        stateLayerType == 'brandColor' && brandColorRippleClass,
      ]"
      :style="{
        left: `${clickPosition.x}px`,
        top: `${clickPosition.y}px`,
      }"
    ></span>
  </span>
</template>
<script lang="ts" setup>
  import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
  import { useMousePressed } from '@vueuse/core'
  import { type BaseStateLayerType } from './base-state-layer-type'
  import { type FABColor } from '../button/fab/fab-color'
  import type { ButtonColor } from '../button/button-color'
  import type { BaseIconButtonType } from '../button/icon-button/base-icon-button-type'
  import { LumiaColor } from '@/modules/lumia/models/lumia-color'

  interface Props {
    type?: ButtonColor | FABColor | BaseIconButtonType | BaseStateLayerType | LumiaColor
    disabled?: boolean
    forcePressed?: boolean
  }

  const props = defineProps<Props>()

  const lumiaStateLayerClass = {
    [LumiaColor.MelancholischerSchiffskapitaen]: {
      overlay:
        'group-hover:bg-melancholischer-schiffskapitaen-80/8 group-active:bg-melancholischer-schiffskapitaen-80/10',
      ripple: 'bg-melancholischer-schiffskapitaen-80/10',
    },
    [LumiaColor.AyahuascaNachher]: {
      overlay: 'group-hover:bg-ayahuasca-nachher-80/8 group-active:bg-ayahuasca-nachher-80/10',
      ripple: 'bg-ayahuasca-nachher-80/10',
    },
    [LumiaColor.Vigoletto]: {
      overlay: 'group-hover:bg-vigoletto-80/8 group-active:bg-vigoletto-80/10',
      ripple: 'bg-vigoletto-80/10',
    },
    [LumiaColor.BlueSpanishEyes]: {
      overlay: 'group-hover:bg-blue-spanish-eyes-80/8 group-active:bg-blue-spanish-eyes-80/10',
      ripple: 'bg-blue-spanish-eyes-80/10',
    },
    [LumiaColor.AyahuascaVorher]: {
      overlay: 'group-hover:bg-ayahuasca-vorher-80/8 group-active:bg-ayahuasca-vorher-80/10',
      ripple: 'bg-ayahuasca-vorher-80/10',
    },
    [LumiaColor.RasenmaeherRueckstaende]: {
      overlay:
        'group-hover:bg-rasenmaeher-rueckstaende-80/8 group-active:bg-rasenmaeher-rueckstaende-80/10',
      ripple: 'bg-rasenmaeher-rueckstaende-80/10',
    },
    [LumiaColor.AfterRooftopParty]: {
      overlay: 'group-hover:bg-after-rooftop-party-80/8 group-active:bg-after-rooftop-party-80/10',
      ripple: 'bg-after-rooftop-party-80/10',
    },
    [LumiaColor.NichtWeiss]: {
      overlay: 'group-hover:bg-nicht-weiss-80/8 group-active:bg-nicht-weiss-80/10',
      ripple: 'bg-nicht-weiss-80/10',
    },
    [LumiaColor.TouristinnenRot]: {
      overlay: 'group-hover:bg-touristinnen-rot-80/8 group-active:bg-touristinnen-rot-80/10',
      ripple: 'bg-touristinnen-rot-80/10',
    },
  } satisfies Record<LumiaColor, { overlay: string; ripple: string }>

  const isLumiaColor = (value: Props['type']): value is LumiaColor =>
    !!value && (Object.values(LumiaColor) as string[]).includes(value)

  const brandColorStateLayerClass = computed(() =>
    isLumiaColor(props.type) ? lumiaStateLayerClass[props.type].overlay : false,
  )

  const brandColorRippleClass = computed(() =>
    isLumiaColor(props.type) ? lumiaStateLayerClass[props.type].ripple : false,
  )

  const stateLayer = useTemplateRef('stateLayer')

  const clickPosition = ref({ x: 0, y: 0 })

  const mouseDown = (event: MouseEvent) => {
    if (stateLayer.value) {
      elementPressed.value = true
      const rect = stateLayer.value.getBoundingClientRect()
      clickPosition.value = {
        x: event.clientX - rect.left, // Relative X position
        y: event.clientY - rect.top, // Relative Y position
      }
    }
    return true
  }

  const mouseUp = () => {
    elementPressed.value = false
  }

  onMounted(() => {
    if (stateLayer.value) {
      stateLayer.value.parentElement?.addEventListener('mousedown', mouseDown)
      stateLayer.value.parentElement?.addEventListener('mouseup', mouseUp)
    }
  })

  const elementPressed = ref(false)

  const { pressed: globalPressed } = useMousePressed()

  watch(globalPressed, () => {
    if (!globalPressed.value) {
      elementPressed.value = false
    }
  })

  const showRipple = computed(() => {
    if (!globalPressed.value) return false
    return elementPressed.value && !props.disabled
  })

  const stateLayerType = computed<BaseStateLayerType>(() => {
    if (props.type == 'elevated') {
      return 'surface'
    }
    if (props.type == 'filled') {
      return 'primary'
    }
    if (props.type == 'tonal') {
      return 'secondary-container'
    }
    if (props.type == 'outlined') {
      return 'surface'
    }
    if (props.type == 'text') {
      return 'surface'
    }
    if (props.type == 'primary') {
      return 'primary-container'
    }
    if (props.type == 'secondary') {
      return 'secondary-container'
    }
    if (props.type == 'filledInverse') {
      return 'on-primary'
    }
    if (props.type == 'tertiary') {
      return 'tertiary-container'
    }
    if (props.type == 'standard') {
      return 'surface'
    }
    if (props.type == 'tonalPrimary') {
      return 'primary-container'
    }
    if (props.type == 'tonalTertiary') {
      return 'tertiary-container'
    }
    if (props.type == 'filledTertiary') {
      return 'tertiary-container'
    }
    if (props.type == 'textOnSurface') {
      return 'surface-neutral'
    }
    if (props.type == 'standardInverse') {
      return 'primary'
    }

    if (isLumiaColor(props.type)) {
      return 'brandColor'
    }

    return 'surface'
  })
</script>
